import React, { useCallback, useEffect, useRef, useState } from 'react';

import { QInput } from '@qualio/ui-components';

type BufferedQInputProps = React.ComponentProps<typeof QInput> & {
  value?: string;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
};

/**
 * A wrapper around QInput that buffers the input value internally to prevent
 * cursor-jumping issues caused by React 18's concurrent rendering.
 *
 * In React 18 with createRoot, Chakra UI v1's FormControl triggers async
 * re-renders on focus, which can reset the cursor position when the controlled
 * value prop is re-applied to the DOM input. This wrapper maintains its own
 * state and only syncs with the external value when the input is not focused.
 */
const BufferedQInput: React.FC<BufferedQInputProps> = ({ value = '', onChange, onBlur, onFocus, ...rest }) => {
  const [localValue, setLocalValue] = useState(value);
  const isFocusedRef = useRef(false);

  // Sync from external value only when the input is not focused
  useEffect(() => {
    if (!isFocusedRef.current) {
      setLocalValue(value);
    }
  }, [value]);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setLocalValue(e.target.value);
      onChange?.(e);
    },
    [onChange],
  );

  const handleFocus = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      isFocusedRef.current = true;
      onFocus?.(e);
    },
    [onFocus],
  );

  const handleBlur = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      isFocusedRef.current = false;
      // Sync with external value on blur to ensure consistency
      setLocalValue(value);
      onBlur?.(e);
    },
    [onBlur, value],
  );

  return <QInput {...rest} value={localValue} onChange={handleChange} onFocus={handleFocus} onBlur={handleBlur} />;
};

export default BufferedQInput;
